/**
 * Pure derivations for the hotel-checkout order summary, shared by the web right-rail
 * (`client/src/views/checkout/HotelBookingSummary.tsx`) and the native summary
 * (`mobile/src/ui/checkout/HotelBookingSummary.tsx`) so the two can't drift — the numbers a guest is
 * shown before committing are legally load-bearing (ELARA-829), so there is exactly one implementation.
 *
 * DOM-free and model-agnostic: everything operates on the booking schemas plus a minimal room-option
 * shape, so it runs identically under React Native.
 */

import type {
  EligibleRoomExtra,
  HotelBookingRoom,
  PriceBreakdown,
  PriceSnapshotItem,
  RoomEligibleExtras,
} from '@elara/domain/models/schemas/booking-schemas';

/** Whole-day count between two `YYYY-MM-DD` dates. Returns 0 for unparseable or non-positive spans. */
export function nightsBetween(checkInDate: string, checkOutDate: string): number {
  const start = new Date(checkInDate).getTime();
  const end = new Date(checkOutDate).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;
  return Math.round((end - start) / 86_400_000);
}

interface LabeledLine {
  label?: string;
  amount: number;
  currency: string;
}

/** Merge labeled charges across rooms by label (e.g. one "City tax" / "Resort fee" line). */
function mergeLabeled(
  map: Map<string, LabeledLine>,
  items: Array<{ label?: string; amount: number; currency?: string }> | undefined,
  currency: string,
): void {
  for (const item of items ?? []) {
    const key = (item.label ?? '').toLowerCase();
    const existing = map.get(key);
    if (existing) existing.amount += item.amount;
    else map.set(key, { label: item.label, amount: item.amount, currency: item.currency || currency });
  }
}

/**
 * Itemized lines from `map`, plus a reconciling "{otherLabel}" line when Sabre's groups don't sum to the
 * aggregate (its tax groups can fall a little short of `Taxes.Amount`) — so the breakdown ALWAYS adds up
 * to the Total. Returns undefined when nothing was itemized (caller falls back to the aggregate line).
 */
function reconcileLabeled(
  map: Map<string, LabeledLine>,
  aggregate: number,
  currency: string,
  otherLabel: string,
): LabeledLine[] | undefined {
  if (map.size === 0) return undefined;
  const lines = [...map.values()];
  const remainder = aggregate - lines.reduce((sum, l) => sum + l.amount, 0);
  if (remainder > 0.01) lines.push({ label: otherLabel, amount: remainder, currency });
  return lines;
}

/**
 * Sum the server-computed per-room `priceSnapshot[].breakdown` into a single subtotal/tax/fees/total
 * breakdown for the displayed rooms. Returns `undefined` when any room lacks a breakdown or the rooms
 * span multiple currencies — in either case the caller falls back to the bottom-line total only. The
 * server owns the Sabre subtraction convention (`subtotal = total − tax − fees`) and the per-room clamp;
 * this just re-sums the rooms actually being shown (so dropping an unavailable room stays correct).
 */
export function computeTotalBreakdown(priceSnapshot: PriceSnapshotItem[]): PriceBreakdown | undefined {
  let subtotal = 0;
  let totalTax = 0;
  let totalFees = 0;
  let total = 0;
  let currency: string | undefined;
  // Itemize taxes (City / State / Lodging…) and mandatory fees ("Resort fee") by label across rooms so the
  // breakdown names what the guest is paying — high city/island taxes become obvious (ELARA-829).
  const taxByLabel = new Map<string, LabeledLine>();
  const feeByLabel = new Map<string, LabeledLine>();
  for (const item of priceSnapshot) {
    const breakdown = item.breakdown;
    if (!breakdown) return undefined;
    if (currency && breakdown.currency !== currency) return undefined;
    currency = breakdown.currency;
    subtotal += breakdown.subtotal;
    totalTax += breakdown.totalTax;
    totalFees += breakdown.totalFees;
    total += breakdown.total;
    mergeLabeled(taxByLabel, breakdown.taxBreakdown, breakdown.currency);
    mergeLabeled(feeByLabel, breakdown.feeBreakdown, breakdown.currency);
  }
  if (!currency) return undefined;
  const taxBreakdown = reconcileLabeled(taxByLabel, totalTax, currency, 'Other taxes');
  const feeBreakdown = reconcileLabeled(feeByLabel, totalFees, currency, 'Other fees');
  return {
    subtotal,
    totalTax,
    totalFees,
    total,
    currency,
    ...(taxBreakdown ? { taxBreakdown } : {}),
    ...(feeBreakdown ? { feeBreakdown } : {}),
  };
}

/** One extra the guest selected, aggregated across rooms, priced for its whole stay. */
export interface AggregatedExtra {
  id: string;
  label: string;
  qty: number;
  /** Total amount in `currency` across all rooms/lines, or undefined when the catalog carries no price. */
  amount?: number;
  currency: string;
}

/** The minimal room-option shape the extras aggregation needs (nights drive `per_night` pricing). */
interface RoomOptionLike {
  nightsCount?: number;
}

/**
 * Aggregate the extras the guest has selected across every room into one list of property-settled
 * lines. Extras are post-stay incidentals collected by the hotel — never folded into the prepaid room
 * subtotals or the grand total — so they're presented as their own "paid at the property" section.
 *
 * Prices come from the server-resolved `eligibleExtras` catalog (keyed by itinerary item), not the live
 * rate's search-time extras, which carry no ids. `per_night` scales by the room's nights, `per_person`
 * by its occupancy.
 */
export function aggregatePropertyExtras(
  rooms: HotelBookingRoom[],
  formRooms: Record<string, { extras: Record<string, number> }> | undefined,
  roomOptionsById: Map<string, RoomOptionLike | undefined>,
  eligibleExtras: RoomEligibleExtras[] = [],
): AggregatedExtra[] {
  if (!formRooms) return [];
  const byId = new Map<string, AggregatedExtra>();
  const catalogByItem = new Map<string, Map<string, EligibleRoomExtra>>(
    eligibleExtras.map((e) => [e.itineraryItemId, new Map(e.extras.map((x) => [x.id, x]))]),
  );

  for (const room of rooms) {
    const formRoom = formRooms[room.itineraryItemId];
    if (!formRoom) continue;
    const roomOption = roomOptionsById.get(room.roomOptionId);
    const catalog = catalogByItem.get(room.itineraryItemId);
    const nights = Math.max(1, roomOption?.nightsCount ?? 1);
    const persons = room.occupancy.adults + room.occupancy.children.length;

    for (const [extraId, qty] of Object.entries(formRoom.extras)) {
      if (qty <= 0) continue;
      const catalogue = catalog?.get(extraId);
      const label = catalogue?.name ?? extraId;
      const currency = catalogue?.currency ?? 'USD';
      const multiplier = catalogue?.scope === 'per_night' ? nights : catalogue?.scope === 'per_person' ? persons : 1;
      const lineAmount = catalogue?.amount != null ? catalogue.amount * qty * multiplier : undefined;

      const existing = byId.get(extraId);
      if (existing) {
        existing.qty += qty;
        if (lineAmount != null) existing.amount = (existing.amount ?? 0) + lineAmount;
      } else {
        byId.set(extraId, { id: extraId, label, qty, amount: lineAmount, currency });
      }
    }
  }

  return [...byId.values()];
}
